import {weakMemo} from '@packages/utilities/memo';
import {emptyObject, emptyArray} from '@packages/utilities/fp/utils';
import {
  type Type,
  type RigidId as Rigid,
  type UnknownId as Unknown,
} from '../DataTypeDefinition';
import {type ExpressionPath} from '../ExpressionPath';
import type {ComponentName} from '../Component';
import * as VariableMap from '../VariableMap';
import * as Variable from '../Variable';
import * as EnvTypes from '../EnvTypes';
import * as Types from '../Types';
import * as TypeImplementation from '../TypeImplementation';
import {
  QualifiedMap,
  type TypeName,
  type Qualified,
  toHumanReadableString,
} from '@packages/systems/wfdl-Qualified';

export type Substitution = Partial<Record<Unknown, Type>>;

/**
 * The type of errors that can arise during type checking
 */
export type Error =
  | {
      type: 'UndefinedValue';
      val: {
        path: ExpressionPath;
        identifier: Variable.VariableType;
      };
    }
  | {
      type: 'UndefinedComponent';
      val: {
        path: ExpressionPath;
        identifier: Qualified<ComponentName>;
      };
    }
  | {
      type: 'UndefinedType';
      val: {
        path: ExpressionPath;
        typeName: Qualified<TypeName>;
      };
    }
  | {
      type: 'NonCustomLiteral';
      val: {
        path: ExpressionPath;
        typeName: Qualified<TypeName>;
      };
    }
  | {
      type: 'NonUniqueDistinct';
      val: {
        path: ExpressionPath;
        typeName: Qualified<TypeName>;
      };
    }
  | {
      type: 'MalformedLiteral';
      val: {
        path: ExpressionPath;
        name: Qualified<TypeName>;
        error: string;
      };
    }
  | {
      type: 'InfiniteType';
      val: {
        path: ExpressionPath;
        type: Type;
      };
    }
  | {
      type: 'RigidOutsideScope';
      val: {
        path: ExpressionPath;
        rigidId: number;
      };
    }
  | {
      type: 'TypesDoNotUnify';
      val: {
        path: ExpressionPath;
        types: [Type, Type];
      };
    }
  | {
      type: 'MissingField';
      val: {
        path: ExpressionPath;
        label: string;
      };
    }
  | {
      type: 'ExtraField';
      val: {
        path: ExpressionPath;
        label: string;
      };
    };

export type Errors = Array<Error>;
export type TypeCheckerError = Error;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
/**
 * The checker state is threaded through all computations of a type-checking
 * session, keeping track of various counters and errors while building up
 * a substitution map that solves unknown types.
 */
export type CheckerState = {
  // Environment kept in state to have simpler signatures for computations,
  // but care must be taken to **not** thread environment changes horizontally.
  // See `withLocalVariable` and `withLocalRigid`.
  env: CheckerEnvironment;
  // Counter used for generating unique unknown types
  nextUnknown: Unknown;
  // Counter used for generating unique rigid types
  nextRigid: Rigid;
  // Append-only mapping of unknowns to the types they resolve to
  substitution: Substitution;
  // Append-only list of type-checking errors
  errors: Errors;
};

export type CheckerEnvironmentContext = {
  // Data types for all the components available
  components: QualifiedMap<ComponentName, Type>;
  // Data Type implementations (not used yet)
  types: QualifiedMap<TypeName, TypeImplementation.TypeImplementation>;
  // Types for the values in scope
  values: VariableMap.Map<EnvTypes.NormalizedEnvType>;
  // The rigid types in scope
  rigids: {[key: number]: true};
};

export type CheckerEnvironment = {
  context: CheckerEnvironmentContext;
  expressionPath: ExpressionPath;
};

export type Checker<a> = (arg1: CheckerState) => [CheckerState, a];

const emptyEnv: CheckerEnvironment = {
  context: {
    components: QualifiedMap.empty(),
    types: QualifiedMap.empty(),
    values: VariableMap.empty(),
    rigids: emptyObject,
  },
  expressionPath: emptyArray,
};

export const emptyCheckerState: CheckerState = {
  env: emptyEnv,
  nextUnknown: 0,
  nextRigid: 0,
  substitution: emptyObject as Substitution,
  errors: emptyArray,
};

export const setEnvironment = weakMemo((env: CheckerEnvironment) =>
  weakMemo(
    (state: CheckerState): CheckerState =>
      env === state.env
        ? state
        : {
            env,
            nextUnknown: state.nextUnknown,
            nextRigid: state.nextRigid,
            substitution: state.substitution,
            errors: state.errors,
          }
  )
);

export const getEnvironment = (state: CheckerState): CheckerEnvironment =>
  state.env;

export const setComponents =
  (components: QualifiedMap<ComponentName, Type>) =>
  (state: CheckerState): CheckerState => {
    const {env} = state;
    if (components === env.context.components) return state;
    return setEnvironment({
      context: {
        components,
        types: env.context.types,
        values: env.context.values,
        rigids: env.context.rigids,
      },
      expressionPath: env.expressionPath,
    })(state);
  };

export const setValues = weakMemo(
  (values: VariableMap.Map<EnvTypes.NormalizedEnvType>) =>
    weakMemo((state: CheckerState): CheckerState => {
      const {env} = state;
      if (values === env.context.values) return state;
      return setEnvironment({
        context: {
          components: env.context.components,
          types: env.context.types,
          values,
          rigids: env.context.rigids,
        },
        expressionPath: env.expressionPath,
      })(state);
    })
);

export const setTypes = weakMemo(
  (types: QualifiedMap<TypeName, TypeImplementation.TypeImplementation>) =>
    weakMemo((state: CheckerState): CheckerState => {
      const {env} = state;
      if (types === env.context.types) return state;
      return setEnvironment({
        context: {
          components: env.context.components,
          types,
          values: env.context.values,
          rigids: env.context.rigids,
        },
        expressionPath: env.expressionPath,
      })(state);
    })
);

export const getNextUnknown = (state: CheckerState): Unknown =>
  state.nextUnknown;

export const getNextRigid = (state: CheckerState): Rigid => state.nextRigid;

export const getSubstitution = (state: CheckerState): Substitution =>
  state.substitution;

export const UndefinedValue = (
  identifier: Variable.VariableType,
  path: ExpressionPath
): Error => ({
  type: 'UndefinedValue',
  val: {path, identifier},
});

export const UndefinedComponent = (
  identifier: Qualified<ComponentName>,
  path: ExpressionPath
): Error => ({
  type: 'UndefinedComponent',
  val: {path, identifier},
});

export const UndefinedType = (
  typeName: Qualified<TypeName>,
  path: ExpressionPath
): Error => ({
  type: 'UndefinedType',
  val: {path, typeName},
});

export const NonCustomLiteral = (
  typeName: Qualified<TypeName>,
  path: ExpressionPath
): Error => ({
  type: 'NonCustomLiteral',
  val: {path, typeName},
});

export const NonUniqueDistinct = (
  typeName: Qualified<TypeName>,
  path: ExpressionPath
): Error => ({
  type: 'NonUniqueDistinct',
  val: {path, typeName},
});

export const MalformedLiteral = (
  name: Qualified<TypeName>,
  error: string,
  path: ExpressionPath
): Error => ({
  type: 'MalformedLiteral',
  val: {path, name, error},
});

export const InfiniteType = (type: Type, path: ExpressionPath): Error => ({
  type: 'InfiniteType',
  val: {path, type},
});

export const RigidOutsideScope = (
  rigidId: number,
  path: ExpressionPath
): Error => ({
  type: 'RigidOutsideScope',
  val: {path, rigidId},
});

export const TypesDoNotUnify = (
  t1: Type,
  t2: Type,
  path: ExpressionPath
): Error => ({
  type: 'TypesDoNotUnify',
  val: {path, types: [t1, t2]},
});

export const MissingField = (label: string, path: ExpressionPath): Error => ({
  type: 'MissingField',
  val: {path, label},
});

export const ExtraField = (label: string, path: ExpressionPath): Error => ({
  type: 'ExtraField',
  val: {path, label},
});

export const getErrors = (state: CheckerState): Array<Error> => state.errors;

export const printError = (err: Error): string =>
  err.type === 'UndefinedValue'
    ? `Undefined value ${Variable.print(err.val.identifier)}.`
    : err.type === 'UndefinedComponent'
      ? `Undefined component ${toHumanReadableString(err.val.identifier)}.`
      : err.type === 'UndefinedType'
        ? `Undefined type ${toHumanReadableString(err.val.typeName)}.`
        : err.type === 'NonCustomLiteral'
          ? `Cannot create Literal values of a Non-Custom Type (${toHumanReadableString(
              err.val.typeName
            )}).`
          : err.type === 'NonUniqueDistinct'
            ? `Cannot create Distinct values of a Non-Unique Type (${toHumanReadableString(
                err.val.typeName
              )}).`
            : err.type === 'MalformedLiteral'
              ? `Malformed Literal value of type ${toHumanReadableString(
                  err.val.name
                )}: ${err.val.error}`
              : err.type === 'InfiniteType'
                ? `Infinity type: ${Types.print(err.val.type)}.`
                : err.type === 'RigidOutsideScope'
                  ? `Found rigid type ${String(err.val.rigidId)} outside its scope.`
                  : err.type === 'TypesDoNotUnify'
                    ? `Expected types ${Types.print(err.val.types[0])} and ${Types.print(
                        err.val.types[1]
                      )} to be the same.`
                    : err.type === 'MissingField'
                      ? `Expected the field ${err.val.label} but it is missing.`
                      : `The field ${err.val.label} was not expected.`;
